import { useCallback, useEffect, useState } from 'react';
import { push } from 'connected-react-router';
import { Box, Heading, Page, Paragraph, TextButton } from '@zillow/constellation-10';
import { useDispatch, useSelector } from 'react-redux';
import { useMarketTrendsFilters } from 'app/shared/hooks/useFilters';
import dateUtils from 'app/shared/utils/dateUtils';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { decodeResourceIdFromUrl } from 'app/shared/utils/urlUtils';
import { fetchTopMarkets } from 'app/shared/flux/actions/AreaActions';
import { resetFilter } from 'app/shared/flux/actions/FilterActions';
import StringConstants from 'app/shared/constants/StringConstants';
import { decorate } from 'app/shared/models/TopMarkets';
import { isEmpty as isMarketTrendsEmpty } from 'app/shared/utils/marketTrendsUtils';

import PageHead from './PageHead';
import SEOFooter from './SEOFooter';
import CompareRent from 'app/shared/components/CompareRent';
import FilterTray from 'app/shared/components/FilterTray';
import { PropertyTypes } from 'app/shared/types/PropertyTypes';
import { Bedrooms } from 'app/shared/types/Bedrooms';
import MarketFAQ from 'app/shared/components/MarketFAQ';
import MarketSummary from 'app/shared/components/MarketSummary';
import MarketTemperature from 'app/shared/components/MarketTemperature';
import MarketUpsell from 'app/shared/components/MarketUpsell';
import RentHistogram from 'app/shared/components/RentHistogram';
import RentLineGraph from 'app/shared/components/RentLineGraph';
import TopMarkets from 'app/shared/components/TopMarkets';
import UpsellCarouselModule from 'app/shared/modules/UpsellCarouselModule';

import type { State } from 'app/shared/types/State';
import { AreaType } from 'app/shared/types/AreaType';
import NearbyAreaTrends from 'app/shared/components/NearbyAreaTrends';
import { logError } from '@zg-rentals/log-error';
import { emitMarketTrendsResultsPageViewEvent } from 'app/shared/constants/MarketTrendsCSEvents';

export const DESCRIPTION_MATRIX = {
  [Bedrooms.ALL]: {
    [PropertyTypes.ALL]: 'all bedrooms and all property types',
    [PropertyTypes.APTSANDCONDOS]: 'apartments',
    [PropertyTypes.HOUSE]: 'houses',
    [PropertyTypes.TOWNHOUSE]: 'townhouses',
  },
  [Bedrooms.STUDIO]: {
    [PropertyTypes.ALL]: 'studio properties',
    [PropertyTypes.APTSANDCONDOS]: 'studio apartments',
    [PropertyTypes.HOUSE]: 'studio houses',
    [PropertyTypes.TOWNHOUSE]: 'studio townhouses',
  },
  [Bedrooms.ONE]: {
    [PropertyTypes.ALL]: '1 bedroom properties',
    [PropertyTypes.APTSANDCONDOS]: '1 bedroom apartments',
    [PropertyTypes.HOUSE]: '1 bedroom houses',
    [PropertyTypes.TOWNHOUSE]: '1 bedroom townhouses',
  },
  [Bedrooms.TWO]: {
    [PropertyTypes.ALL]: '2 bedroom properties',
    [PropertyTypes.APTSANDCONDOS]: '2 bedroom apartments',
    [PropertyTypes.HOUSE]: '2 bedroom houses',
    [PropertyTypes.TOWNHOUSE]: '2 bedroom townhouses',
  },
  [Bedrooms.THREE]: {
    [PropertyTypes.ALL]: '3 bedroom properties',
    [PropertyTypes.APTSANDCONDOS]: '3 bedroom apartments',
    [PropertyTypes.HOUSE]: '3 bedroom houses',
    [PropertyTypes.TOWNHOUSE]: '3 bedroom townhouses',
  },
  [Bedrooms.FOURPLUS]: {
    [PropertyTypes.ALL]: '4+ bedroom properties',
    [PropertyTypes.APTSANDCONDOS]: '4+ bedroom apartments',
    [PropertyTypes.HOUSE]: '4+ bedroom houses',
    [PropertyTypes.TOWNHOUSE]: '4+ bedroom townhouses',
  },
};

const MarketTrendsResultsPage = () => {
  const dispatch = useDispatch();
  const { isMobile, screenWidth } = useSelector((state: State) => state.app.device);
  const marketTrends = useSelector((state: State) => state.marketTrends);
  const user = useSelector((state: State) => state.user);
  const faqParams = useSelector((state: State) => state.unfilteredMarketTrends);
  const topMarkets = useSelector((state: State) => state.topMarkets);
  const pathname = useSelector((state: State) => state.router.location.pathname);
  const queryString = useSelector((state: State) => state.router.location.search);
  const filter = useSelector((state: State) => state.filter);
  const medianRentByBedroom = useSelector((state: State) => state.medianRentByBedroom);
  const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes } = useMarketTrendsFilters(filter, dispatch);
  const [loading, setLoading] = useState(false);
  const chooseAnotherArea: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
    async (e) => {
      e.preventDefault();
      if (!(propertyTypes === PropertyTypes.ALL && bedrooms === Bedrooms.ALL)) {
        await dispatch(fetchTopMarkets({}))
          // @ts-ignore
          .catch((err) => {
            logError({
              error: err,
              errorType: 'MarketTrends',
              context: {
                details: 'Market Stats Results Page: Error fetching Top Markets (CSR)',
                root: 'pages/MarketTrendsResultsPage#chooseAnotherArea',
              },
            });
          });
        dispatch(resetFilter());
      }
      dispatch(push(pathsManifest.MarketTrendsPage + '/'));
    },
    [bedrooms, propertyTypes, dispatch],
  );

  const topRentalMarkets = topMarkets.topRentalMarkets.map((market) => decorate(market, queryString));
  const {
    areaName,
    areaType,
    date,
    zillowRentalsSrpUrl,
    medianRent,
    minRent,
    maxRent,
    monthlyChange,
    yearlyChange,
    avgDaysOnMarket,
    availableRentals,
    temperature,
    rentCounts,
    curYearRentPrices,
    prevYearRentPrices,
    nearbyAreas,
    zipcodesInCity,
    nearbyAreaTrends,
  } = marketTrends;
  const zipcodes = areaType === AreaType.ZIP ? [] : zipcodesInCity;
  const hasData = !isMarketTrendsEmpty(marketTrends);
  const hasMarketSummary = !!(medianRent || monthlyChange || yearlyChange || avgDaysOnMarket || availableRentals);
  const resourceId = decodeResourceIdFromUrl(pathname);
  const displayDate = dateUtils.displayDate(date);
  const isSmallScreen = isMobile || screenWidth === 'md';

  const chooseAnotherAreaButton = (
    <TextButton asChild textStyle="body-sm-bold" css={{ marginBottom: '400', height: '600' }} disabled={loading}>
      <a href={pathsManifest.MarketTrendsPage + '/'} onClick={chooseAnotherArea}>
        Choose another area
      </a>
    </TextButton>
  );

  const navButtons = (
    <>
      {chooseAnotherAreaButton}
      <div>
        <FilterTray
          bedrooms={bedrooms}
          setBedrooms={setBedrooms}
          propertyTypes={propertyTypes}
          setPropertyTypes={setPropertyTypes}
          loading={loading}
          setLoading={setLoading}
          isMobile={isMobile}
        />
        {displayDate && (
          <Paragraph
            textStyle="body-xs"
            css={{ textAlign: isSmallScreen ? 'left' : 'right', marginTop: '-200', marginBottom: '400' }}
          >
            Last updated {displayDate}
          </Paragraph>
        )}
      </div>
    </>
  );

  useEffect(() => {
    emitMarketTrendsResultsPageViewEvent(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageHead
        areaName={areaName}
        hasData={hasData}
        faqParams={faqParams}
        canonicalURL={`${StringConstants.ZILLOW_URL}${pathname}`}
        // @ts-ignore
        medianRent={medianRent}
        medianRentByBedroom={medianRentByBedroom}
      />
      <Page.Root css={{ backgroundColor: 'bg.neutral' }}>
        <Page.Content
          css={{
            ...(loading && { opacity: '50%' }),
          }}
        >
          {hasData && typeof areaName === 'string' ? (
            <>
              <Heading level={1} textStyle="responsive.heading-lg" fontFamily="heading-alt">
                {areaName} rental market
              </Heading>

              <Heading level={2} textStyle="responsive.heading-md" fontFamily="heading-alt">
                What is the average rent in {areaName}?
              </Heading>

              {isSmallScreen ? (
                navButtons
              ) : (
                <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  {navButtons}
                </Box>
              )}
              {hasMarketSummary && (
                <MarketSummary
                  market={areaName}
                  filterDescription={DESCRIPTION_MATRIX[bedrooms][propertyTypes]}
                  medianRent={medianRent}
                  monthlyChange={monthlyChange}
                  yearlyChange={yearlyChange}
                  avgDaysOnMarket={avgDaysOnMarket}
                  availableRentals={availableRentals}
                  isSmallScreen={isSmallScreen}
                />
              )}
              <Box css={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '600', marginTop: '600' }}>
                {rentCounts.length > 0 && (
                  <RentHistogram
                    rentCounts={rentCounts}
                    minRent={minRent}
                    maxRent={maxRent}
                    filterDescription={DESCRIPTION_MATRIX[bedrooms][propertyTypes]}
                    isSmallScreen={isSmallScreen}
                  />
                )}
                {(curYearRentPrices.length > 0 || prevYearRentPrices.length > 0) && (
                  <RentLineGraph
                    prevYear={prevYearRentPrices}
                    curYear={curYearRentPrices}
                    yearlyChange={yearlyChange}
                    isSmallScreen={isSmallScreen}
                  />
                )}
                {temperature !== null && (
                  <MarketTemperature areaName={areaName} temp={temperature} isSmallScreen={isSmallScreen} />
                )}
                {medianRent !== null && (
                  <CompareRent
                    market={{ name: areaName, medianRent, resourceId }}
                    isSmallScreen={isSmallScreen}
                    isMobile={isMobile}
                  />
                )}
              </Box>
              {zillowRentalsSrpUrl && (
                <MarketUpsell name={areaName} isSmallScreen={isSmallScreen} href={zillowRentalsSrpUrl} />
              )}
              <UpsellCarouselModule addMarginTop={true} />
              {nearbyAreaTrends.length ? (
                <NearbyAreaTrends
                  areaName={areaName}
                  nearbyAreaTrends={nearbyAreaTrends}
                  isSmallScreen={isSmallScreen}
                  isMobile={isMobile}
                />
              ) : null}
              <MarketFAQ marketTrends={faqParams} medianRentByBedroom={medianRentByBedroom} isMobile={isMobile} />
              {nearbyAreas.length || zipcodes.length ? (
                <SEOFooter areaName={areaName} zipcodesInCity={zipcodes} nearbyAreas={nearbyAreas} />
              ) : null}
            </>
          ) : (
            <>
              <Heading level={1} textStyle="responsive.heading-lg" fontFamily="heading-alt">
                We&#39;re sorry!
              </Heading>
              {areaName === null ? (
                <Paragraph css={{ marginTop: '600' }}>
                  We can&#39;t seem to find any data that matches your search. Choose another area or browse top rental
                  markets below.
                </Paragraph>
              ) : (
                <Paragraph css={{ marginTop: '600' }}>
                  We can&#39;t seem to find any data that matches your search for{' '}
                  <strong>
                    <i>{areaName}</i>
                  </strong>
                  . Choose another area or browse top rental markets below.
                </Paragraph>
              )}
              {chooseAnotherAreaButton}
              <TopMarkets markets={topRentalMarkets} isSmallScreen={isSmallScreen} isMobile={isMobile} />
            </>
          )}
        </Page.Content>
      </Page.Root>
    </>
  );
};

export default MarketTrendsResultsPage;
